import { Avatar, Box, Tooltip } from "@mui/material";
import { httpService } from "./http.service";

export function normalizeRunAtTime(raw) {
  const s = String(raw || "").trim();
  if (!s) return { normalized: "", kind: "immediate" };

  const m = /^(\d{2}):(\d{2})$/.exec(s);
  if (!m) return { normalized: "", kind: "immediate" };

  let hh = Number(m[1]);
  let mm = Number(m[2]);

  if (Number.isNaN(hh) || Number.isNaN(mm)) {
    return { normalized: "", kind: "immediate" };
  }

  // garante range
  hh = Math.min(23, Math.max(0, hh));
  mm = Math.min(59, Math.max(0, mm));

  // 00:00 => imediato
  if (hh === 0 && mm === 0) {
    return { normalized: "", kind: "immediate" };
  }

  const totalMinutes = hh * 60 + mm;

  const normalized = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;

  // decisão SOMENTE pelo total de minutos
  if (totalMinutes > 59) {
    return { normalized, kind: "hours" };
  }

  return { normalized, kind: "minutes" };
}

export function formatRunAtTimeHuman(normalized, t) {
  if (!normalized) return "";

  const [hhStr, mmStr] = normalized.split(":");
  const hh = Number(hhStr);
  const mm = Number(mmStr);

  const parts = [];

  if (hh > 0) {
    parts.push(
      hh === 1
        ? t("builder.time.hour", { count: 1 })
        : t("builder.time.hours", { count: hh })
    );
  }

  if (mm > 0) {
    parts.push(
      mm === 1
        ? t("builder.time.minute", { count: 1 })
        : t("builder.time.minutes", { count: mm })
    );
  }

  return parts.join(` ${t("builder.time.and")} `);
}

const getMemberById = (members, id) => {
  if (!members) return;
  const memberId = members.find((m) => m?.userId?._id === id) || null
  return memberId;
};

export const getMemberLabel = (members, id) => {
  const m = getMemberById(members, id);
  return m?.userId?.name || m?.userId?.email || "Membro";
};

export const getMemberAvatar = (members, id) => {
  const m = getMemberById(members, id);
  return m?.userId?.imgURL || "";
};

export function buildAutomationSentenceJSX({ automation, board, groupsFromStore, members, t }) {
  const cols = board?.cmpsOrder || [];
  const column = cols.find((c) => c?.id === automation?.columnId) || null;

  const colLabel = column?.label || automation?.columnId || "-";
  const isStatus = column?.type === "status-picker";
  const isDate = column?.type === "date-picker";
  const isStatusTimeByStatus = automation?.action === "track_status_time_by_status";
  const isStatusTime = automation?.action === "track_status_time" || isStatusTimeByStatus;

  // valor apenas para status-picker
  const valueObj =
    isStatus && column?.labels
      ? column.labels.find((l) => l?.id === automation?.value) || null
      : null;
  const stopValueObj =
    isStatus && column?.labels
      ? column.labels.find((l) => l?.id === automation?.stopValue) || null
      : null;

  const actionLabel = t?.(`builder.${automation?.action}`, {
    defaultValue: automation?.action || "-",
  });

  // ---- grupo (move_to_group)
  let groupLabel = null;
  if (automation?.action === "move_to_group") {
    const groupId = automation?.groupId;

    const fromBoard =
      Array.isArray(board?.groups) && typeof board.groups?.[0] === "object"
        ? board.groups.find((g) => g?._id === groupId)
        : null;

    const fromStore = Array.isArray(groupsFromStore)
      ? groupsFromStore.find((g) => g?._id === groupId)
      : null;

    groupLabel = (fromBoard || fromStore)?.title || groupId || "-";
  }

  // ---- membros (notify_someone) - multi
  const membersIds = Array.isArray(automation?.membersId)
    ? automation.membersId
    : automation?.membersId
      ? [automation.membersId]
      : [];

  const notifyAvatars =
    membersIds.length > 0 ? (
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
        {membersIds.map((id, idx) => {
          const name = getMemberLabel(members, id);
          const avatar = getMemberAvatar(members, id);

          return (
            <Tooltip key={id || idx} title={name} arrow>
              <Avatar
                src={avatar || undefined}
                alt={name}
                sx={{
                  width: 26,
                  height: 26,
                  ml: idx === 0 ? 0 : -0.75, // sobreposição leve
                  border: "2px solid rgba(255,255,255,0.65)",
                  boxSizing: "content-box",
                  cursor: "default",
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
    ) : (
      <Box component="span" sx={{ fontWeight: 900 }}>-</Box>
    );

  // ---- runAt
  const { normalized, kind } = normalizeRunAtTime(automation?.runAtTime);
  const humanTime = formatRunAtTimeHuman(normalized, t);

  const runAtText = !normalized
    ? t?.("builder.runAt.immediately")
    : kind === "hours"
      ? t?.("builder.runAt.afterHours", { time: humanTime })
      : t?.("builder.runAt.afterMinutes", { time: humanTime });

  const scopeGroupIds = Array.isArray(automation?.scopeGroupIds)
    ? automation.scopeGroupIds
    : [];
  const scopeLabels = scopeGroupIds.map((groupId) => {
    const fromBoard =
      Array.isArray(board?.groups) && typeof board.groups?.[0] === "object"
        ? board.groups.find((group) => group?._id === groupId)
        : null;
    const fromStore = Array.isArray(groupsFromStore)
      ? groupsFromStore.find((group) => group?._id === groupId)
      : null;
    return (fromBoard || fromStore)?.title || groupId;
  });

  return (
    <>
      {t?.("builder.when", { defaultValue: "Quando a coluna" })}{" "}
      <Box component="span" sx={{ fontWeight: 900 }}>
        {colLabel}
      </Box>{" "}

      {/* STATUS: "mudar para o valor X" */}
      {isStatus && !isStatusTimeByStatus ? (
        <>
          {t?.("builder.changesTo", { defaultValue: "mudar para o valor" })}{" "}
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              fontWeight: 700,
              px: 1,
              py: 0.45,
              borderRadius: "8px",
              backgroundColor: valueObj?.color || "rgba(255,255,255,0.10)",
              color: "#fff",
              lineHeight: 1,
            }}
          >
            {valueObj?.title || automation?.value || "-"}
          </Box>{" "}
          {automation?.action === "track_status_time" && (
            <>
              {t?.("builder.untilChangesTo", { defaultValue: "até alterar para" })}{" "}
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  fontWeight: 700,
                  px: 1,
                  py: 0.45,
                  borderRadius: "8px",
                  backgroundColor: stopValueObj?.color || "rgba(255,255,255,0.10)",
                  color: "#fff",
                  lineHeight: 1,
                }}
              >
                {stopValueObj?.title || automation?.stopValue || "-"}
              </Box>{" "}
            </>
          )}
        </>
      ) : (
        /* DATE: apenas "mudar" (sem valor) */
        <>
          {isDate ? (
            <>
              {t?.("builder.reachDeadline", { defaultValue: "atingir o prazo em" })}{" "}
            </>
          ) : (
            <>
              {t?.("builder.changes", { defaultValue: "mudar" })}{" "}
            </>
          )}
        </>
      )}

      {t?.("builder.then", { defaultValue: ", então devemos" })}{" "}

      {/* ACTION: label humanizado + complementos */}
      {automation?.action === "notify_someone" ? (
        <>
          <Box component="span" sx={{ fontWeight: 900 }}>
            {t?.("builder.notify_someone", { defaultValue: "Notificar" })}
          </Box>{" "}
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", ml: 0.75 }}>
            {notifyAvatars}
          </Box>
        </>
      ) : (
        <>
          <Box component="span" sx={{ fontWeight: 900 }}>
            {actionLabel}
          </Box>

          {automation?.action === "move_to_group" && (
            <>
              {" "}
              <Box component="span" sx={{ fontWeight: 900 }}>
                {groupLabel}
              </Box>
            </>
          )}
        </>
      )}{" "}

      {automation?.action !== "track_status_time" && !isStatusTimeByStatus && (
        <>
          {t("builder.runAt.label")}{" "}
          <Box component="span" sx={{ fontWeight: 900 }}>
            {runAtText}
          </Box>
        </>
      )}
      {isStatusTime && (
        <>
          {" "}Início:{" "}
          <Box component="span" sx={{ fontWeight: 900 }}>
            {scopeLabels.length ? scopeLabels.join(", ") : "Todos os grupos"}
          </Box>
        </>
      )}
      .
    </>
  );
}

export function formatSize(bytes) {
  const n = Number(bytes) || 0;
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${n} B`;
}
// calcula dias restantes até a exclusão definitiva
export function daysUntilDeletion(updatedAtISO, opts = {}) {
  const { retentionDays = 30, timeZone = 'America/Sao_Paulo', now = new Date() } = opts;

  const updated = new Date(updatedAtISO);
  if (isNaN(updated.getTime())) return null; // data inválida

  const startOfDayUTC = (d) => {
    const parts = new Intl.DateTimeFormat('pt-BR', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(d);

    const y = Number(parts.find(p => p.type === 'year').value);
    const m = Number(parts.find(p => p.type === 'month').value);
    const day = Number(parts.find(p => p.type === 'day').value);

    return Date.UTC(y, m - 1, day);
  };

  const msPerDay = 24 * 60 * 60 * 1000;
  const elapsedDays = Math.floor((startOfDayUTC(now) - startOfDayUTC(updated)) / msPerDay);

  // dias restantes até completar retentionDays (entre 0 e retentionDays)
  const remaining = retentionDays - elapsedDays;
  return Math.max(0, Math.min(retentionDays, remaining));
}

// retorna a data restante para exclusão definitiva
function daysUntilDeletionLabel(iso) {
  const d = daysUntilDeletion(iso);
  if (d === null) return 'data inválida';
  if (d === 0) return 'exclusão iminente';
  return `${d} dia${d === 1 ? '' : 's'} restantes para exclusão automática`;
};


function makeId(length = 6) {
  var txt = "";
  var possible =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

  for (var i = 0; i < length; i++) {
    txt += possible.charAt(Math.floor(Math.random() * possible.length));
  }
  return txt;
}

function makeLorem(size = 100) {
  var words = [
    "The sky",
    "above",
    "the port",
    "was",
    "the color of television",
    "tuned",
    "to",
    "a dead channel",
    ".",
    "All",
    "this happened",
    "more or less",
    ".",
    "I",
    "had",
    "the story",
    "bit by bit",
    "from various people",
    "and",
    "as generally",
    "happens",
    "in such cases",
    "each time",
    "it",
    "was",
    "a different story",
    ".",
    "It",
    "was",
    "a pleasure",
    "to",
    "burn",
  ];
  var txt = "";
  while (size > 0) {
    size--;
    txt += words[Math.floor(Math.random() * words.length)] + " ";
  }
  return txt;
}

function getRandomIntInclusive(min, max) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min; //The maximum is inclusive and the minimum is inclusive
}

function debounce(func, timeout = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
}

function getMonthName(date) {
  const monthNames = [
    "Jan`",
    "Feb`",
    "March",
    "April",
    "May",
    "June",
    "July",
    "Aug`",
    "Sep`",
    "Oct`",
    "Nov`",
    "Dec`",
  ];
  return monthNames[date.getMonth()];
}

function saveToStorage(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

function loadFromStorage(key) {
  const data = localStorage.getItem(key);
  return data ? JSON.parse(data) : undefined;
}

function getColors() {
  return [
    "#a25ddc",
    "#FBBC04",
    "#F1E4DE",
    "#FDCFE8",
    "#F28B82",
    "#FFF475",
    "#CCFF90",
    "#CBF0F8",
    "#A7FFEB",
    "#D7AEFB",
    "#E6C9A8",
    "#E8EAED",
  ];
}

function getRandomColor() {
  let maxVal = 0xffffff;
  let randomNumber = Math.random() * maxVal;
  randomNumber = Math.floor(randomNumber);
  randomNumber = randomNumber.toString(16);
  let randColor = randomNumber.padStart(6, 0);
  return `#${randColor.toUpperCase()}`;
}

function calculateTime(time) {
  if (!time) return "N/I";

  const timestamp = new Date(time).getTime();

  if (isNaN(timestamp)) return "N/I";

  const currentTime = new Date().getTime();
  const timeDiff = Math.floor((currentTime - timestamp) / 60000);

  if (timeDiff >= 60 * 24 * 7) {
    const weeks = Math.floor(timeDiff / (60 * 24 * 7));
    return `${weeks}w`;
  } else if (timeDiff >= 60 * 24) {
    const days = Math.floor(timeDiff / (60 * 24));
    return `${days}d`;
  } else if (timeDiff >= 60) {
    const hours = Math.floor(timeDiff / 60);
    return `${hours}h`;
  } else if (timeDiff >= 1) {
    return `${timeDiff}m`;
  } else {
    return "now";
  }
}

function normalizeImagesForStorage(delta, accountId) {
  if (!delta?.ops?.length) return delta;
  const cloned = JSON.parse(JSON.stringify(delta));
  const re = /\/uploads\/[^/]+\/files\/([0-9a-fA-F-]{36})\.(\w+)/; // .../uploads/<account>/files/<uuid>.<ext>

  cloned.ops = cloned.ops.map(op => {
    if (typeof op.insert === 'object' && op.insert?.image) {
      // tratando imagem como string (URL completa)
      if (typeof op.insert.image === 'string') {
        const m = re.exec(op.insert.image);
        if (m) {
          const [, uuid, ext] = m;
          op.insert.image = { uuid, ext: ext.toLowerCase() };
          if (op.attributes?.id) delete op.attributes.id;
        }
      }
    }
    return op;
  });

  return cloned;
}


function getFormattedDate(timestamp) {
  const date = new Date(timestamp);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

function updateItemInList(list, updatedItem) {
  return list.map(item => item._id === updatedItem._id ? updatedItem : item);
}

function removeItemFromList(list, itemId) {
  return list.filter(item => item._id !== itemId);
}

let cachedBaseUrl = null;

async function getBackendBaseUrl() {
  if (cachedBaseUrl) return cachedBaseUrl;

  try {
    const response = await httpService.get("base-url");
    cachedBaseUrl = response.baseUrl;
    return cachedBaseUrl;
  } catch (err) {
    console.error("Erro ao buscar base URL do backend:", err);
    throw err;
  }
}

function isWithinFifteenDays(dateLike) {
  if (!dateLike) return false;
  const created = new Date(dateLike);
  if (isNaN(created)) return false;
  const now = new Date();
  const diffMs = now.getTime() - created.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays <= 15 && diffDays >= 0;
}

function isAccountValid(account) {
  if (!account) return false;

  const noPlanAndRecent = !account.plan?._id && isWithinFifteenDays(account.creationDate);
  const hasActivePlan = account.plan?.active;

  return noPlanAndRecent || hasActivePlan;
}

// verifica se duas listas são iguais
function sameArray(a = [], b = []) {
  if (a.length !== b.length) return false;
  const sa = [...a].map(String).sort();
  const sb = [...b].map(String).sort();
  for (let i = 0; i < sa.length; i++) if (sa[i] !== sb[i]) return false;
  return true;
};

// verifica diferença dos arrays que não estao presentes e os que estão da lista a para a b
function diffArrays(prev = [], next = []) {
  const A = new Set(prev.map(String));
  const B = new Set(next.map(String));
  const removed = [...A].filter(x => !B.has(x));
  const added = [...B].filter(x => !A.has(x));
  return { added, removed };
};

// iniciais
export function initialsOf(name, email) {
  const src = (name || '').trim() || (email || '').trim();
  if (!src) return '?';
  const parts = src.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src[0].toUpperCase();
};

function digitsOnly(input) {
  return String(input || "").replace(/\D/g, "");
}

function formatPhoneForUiByLang(raw, lang) {
  const d = digitsOnly(raw);

  if (lang === "pt-BR" || lang === "pt") {
    const dd = d.slice(0, 2);
    const rest = d.slice(2, 11);

    if (!d.length) return "";
    if (d.length <= 2) return `(${dd}`;
    if (rest.length <= 4) return `(${dd}) ${rest}`;

    if (rest.length <= 8) {
      const a = rest.slice(0, 4);
      const b = rest.slice(4, 8);
      return `(${dd}) ${a}${b ? `-${b}` : ""}`;
    } else {
      const a = rest.slice(0, 5);
      const b = rest.slice(5, 9);
      return `(${dd}) ${a}${b ? `-${b}` : ""}`;
    }
  }

  if (lang === "en" || lang === "en-US") {
    const a = d.slice(0, 3);
    const b = d.slice(3, 6);
    const c = d.slice(6, 10);

    if (!d.length) return "";
    if (d.length <= 3) return `(${a}`;
    if (d.length <= 6) return `(${a}) ${b}`;
    return `(${a}) ${b}-${c}`;
  }
  if (!d.length) return "";
  const chunks = [];
  let i = 0;
  while (i < d.length) {
    const size = i === 0 ? 3 : 3;
    chunks.push(d.slice(i, i + size));
    i += size;
  }
  return chunks.join(" ");
}

function getPhonePlaceholderByLang(lang) {
  if (lang === "pt-BR" || lang === "pt") return "(11) 99999-9999";
  if (lang === "en" || lang === "en-US") return "(555) 123-4567";
  if (lang === "es" || lang === "es-ES") return "+34 612 345 678";
  return "+00 000 000 000";
}

export function userAvatarSrc({
  imgURL,
  BASE_URL,
  accountId,
  guestSrc,
  version,
  folder = "profile-pictures"
}) {
  const v = String(imgURL || "").trim();

  if (!v) return guestSrc;

  // URLs completas
  if (/^(https?:\/\/|data:|blob:)/i.test(v)) {
    if (!version) return v;
    const sep = v.includes("?") ? "&" : "?";
    return `${v}${sep}v=${encodeURIComponent(String(version))}`;
  }

  const safeBase = String(BASE_URL || "").replace(/\/+$/, "");
  const safeAccount = String(accountId || "").replace(/^:+/, "");
  const safeFile = v.replace(/^\/+/, "");
  const safeFolder = String(folder || "profile-pictures").replace(/^\/+|\/+$/g, "");

  if (!safeBase || !safeAccount) return guestSrc;
  
  const url = `${safeBase}/uploads/${safeAccount}/${safeFolder}/${safeFile}`;
  if (!safeBase || !safeAccount) return guestSrc;
  if (!version) return url;

  return `${url}?v=${encodeURIComponent(String(version))}`;
}

export const utilService = {
  makeId,
  initialsOf,
  makeLorem,
  getRandomIntInclusive,
  debounce,
  saveToStorage,
  loadFromStorage,
  getMonthName,
  getColors,
  getRandomColor,
  calculateTime,
  getFormattedDate,
  updateItemInList,
  removeItemFromList,
  getBackendBaseUrl,
  normalizeImagesForStorage,
  sameArray,
  diffArrays,
  isAccountValid,
  daysUntilDeletionLabel,
  formatSize,
  isWithinFifteenDays,
  getPhonePlaceholderByLang,
  formatPhoneForUiByLang
};
